'use client'

import { Button, Modal, ModalBody, ModalContent } from '@nextui-org/react'
import { CircleXIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'

/**
 * CMS value first, bundled English for the keys the CMS lacks — the `useRedeemT()` pattern, for the
 * same reason: no `l_config_language` row exists for these yet, and the previous copies of this
 * modal hardcoded English on a seven-locale site.
 */
const FALLBACK_EN = {
  MAINTENANCE_TITLE: 'System offline for maintenance',
  MAINTENANCE_MESSAGE:
    "We're sorry, our website is temporarily closed for site enhancements. We'll be back soon!",
  MAINTENANCE_BUTTON_OK: 'OK'
} as const

interface Props {
  isOpen: boolean
  /** The API's own localised message. Falls back to the CMS copy when the API sends none. */
  message?: string
  /** Omit where the page behind the modal is locked — there is nothing to dismiss back to. */
  onClose?: () => void
}

/**
 * A hard block, not a notice: no close button, and ESC and backdrop-click are both disabled. The
 * only way out is `onClose`, which the caller supplies only when the page behind stays usable.
 */
export function MaintenanceModal({ isOpen, message, onClose }: Props) {
  const t = useTranslations()

  const copy = (key: keyof typeof FALLBACK_EN): string => {
    const path = `Menu.${key}`

    try {
      if (t.has(path)) return t(path)
    } catch {
      // A malformed CMS entry must not leave the notice blank.
    }

    return FALLBACK_EN[key]
  }

  return (
    <Modal
      isOpen={isOpen}
      size='sm'
      radius='sm'
      placement='center'
      hideCloseButton
      isDismissable={false}
      isKeyboardDismissDisabled
      // Above the promotion popup, which the promotion CTA can be rendered inside.
      classNames={{ wrapper: 'z-[9999]' }}>
      <ModalContent>
        <ModalBody className='py-6'>
          <div className='grid gap-2 text-center'>
            <CircleXIcon className='mx-auto size-20 stroke-1 text-red-500' />
            <div className='text-xl font-semibold text-zinc-800'>{copy('MAINTENANCE_TITLE')}</div>
            <p className='text-sm font-normal text-zinc-600'>
              {message || copy('MAINTENANCE_MESSAGE')}
            </p>
          </div>

          {onClose && (
            <Button
              className='mx-auto w-32 bg-red-600 text-lg text-white'
              size='sm'
              radius='sm'
              onPress={onClose}>
              {copy('MAINTENANCE_BUTTON_OK')}
            </Button>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  )
}
